/* eslint-disable no-shadow */

import Vue from 'vue';

import Common from '@/store/common';
import Store from '@/store/index';

const regexValidator = (value) => {
  try {
    /* eslint-disable no-new */
    new RegExp(value);
    return [];
  } catch (error) {
    return [String(error).replace('SyntaxError: ', '')];
  }
};

const isCurrentFieldType = (model, fieldTypes) => {
  if (fieldTypes === 'all') {
    return model.field_type !== 'statictext';
  }
  return fieldTypes.includes(model.field_type);
};

const state = {
  fields: [],
  activeField: 0,
  fieldsLoading: false,
  fieldSchema: {
    basicInfo: {
      includeAll: true,
      fields: [
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Title',
          model: 'title',
          validator: 'string',
          required: true,
        },
        {
          type: 'customSelect',
          styleClasses: 'form__field',
          label: 'Field Type',
          model: 'field_type',
          values: () => {
            return Object.keys(Store.state.field.inputTypes).map((key) => {
              return { id: key, name: Store.state.field.inputTypes[key].name };
            }).filter(val => val.id !== 'submit' && val.id !== 'Button');
          },
          validator: 'string',
          required: true,
        },
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Hint',
          model: 'hint',
          validator: 'string',
          visible: model => isCurrentFieldType(model, 'all'),
        },
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Default Value',
          model: 'default',
          visible: model => isCurrentFieldType(model, 'all'),
        },
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Placeholder',
          model: 'placeholder',
          visible: model => isCurrentFieldType(model, Store.state.field.fieldTypesWithInputBox),
        },
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Start Label',
          model: 'start_label',
          validator: 'string',
          visible: model => isCurrentFieldType(model, ['opinionscale', 'opinionrange']),
        },
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'End Label',
          model: 'end_label',
          validator: 'string',
          visible: model => isCurrentFieldType(model, ['opinionscale', 'opinionrange']),
        },
        {
          type: 'customInput',
          inputType: 'number',
          styleClasses: 'form__field',
          label: 'Min Value',
          model: 'min_value',
          hint: 'Defaults to 0',
          validator: 'number',
          visible: model => isCurrentFieldType(model, ['slider']),
        },
        {
          type: 'customInput',
          inputType: 'number',
          styleClasses: 'form__field',
          label: 'Max Value',
          model: 'max_value',
          hint: 'Defaults to 100',
          validator: 'number',
          visible: model => isCurrentFieldType(model, ['slider']),
        },
        {
          type: 'customInput',
          inputType: 'number',
          styleClasses: 'form__field',
          label: 'Step Size',
          model: 'step_size',
          hint: 'Defaults to 1',
          validator: 'number',
          visible: model => isCurrentFieldType(model, ['slider']),
        },
        {
          type: 'customInput',
          inputType: 'number',
          styleClasses: 'form__field',
          label: 'Position',
          model: 'position',
          hint: 'Will be set automatically if not filled in',
        },
        {
          type: 'customTextArea',
          styleClasses: 'form__field',
          label: 'Static Text',
          model: 'static_text',
          validator: 'string',
          rows: 8,
          visible: model => isCurrentFieldType(model, ['statictext']),
        },
        {
          type: 'customInput',
          inputType: 'number',
          styleClasses: 'form__field',
          label: 'Max Number of Selections',
          model: 'max_option_selections',
          hint: 'Limit the number of options the user can select. Enter 0 for unlimited.',
          visible: model => isCurrentFieldType(model, ['checkbox']),
        },
        {
          type: 'customCheckbox',
          styleClasses: 'form__field',
          label: 'Required?',
          model: 'required',
          visible: model => isCurrentFieldType(model, 'all'),
        },
      ],
    },
    validation: {
      includeAll: true,
      fields: [
        {
          type: 'customInput',
          inputType: 'number',
          styleClasses: 'form__field',
          label: 'Minimum Length',
          model: 'min_length',
          min: 1,
          visible: model => !isCurrentFieldType(model, ['slider', 'integer']),
        },
        {
          type: 'customInput',
          inputType: 'number',
          styleClasses: 'form__field',
          label: 'Maximum Length',
          model: 'max_length',
          min: 1,
          visible: model => !isCurrentFieldType(model, ['slider', 'integer']),
        },
        {
          type: 'customInput',
          inputType: 'number',
          styleClasses: 'form__field',
          label: 'Min Value',
          model: 'min_value',
          validator: 'number',
          visible: model => isCurrentFieldType(model, ['integer']),
        },
        {
          type: 'customInput',
          inputType: 'number',
          styleClasses: 'form__field',
          label: 'Max Value',
          model: 'max_value',
          validator: 'number',
          visible: model => isCurrentFieldType(model, ['integer']),
        },
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Validation Regex',
          model: 'validation',
          validator: regexValidator,
        },
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Regex Hint',
          model: 'regex_hint',
          validator: 'string',
        },
      ],
    },
    all: {
      fields: [
        {
          model: '',
          type: 'customSubmit',
          styleClasses: 'form__submit',
          buttonText: 'Add Field',
          onlyOn: 'new',
          onSubmit: (model, schema, context, form) => {
            context.addForm(model, form);
          },
          validateBeforeSubmit: true,
        },
        {
          model: '',
          type: 'customSubmit',
          styleClasses: 'form__submit',
          buttonText: 'Save Field',
          onlyOn: 'edit',
          onSubmit: (model, schema, context, form) => {
            context.editForm(model, form);
          },
          validateBeforeSubmit: true,
        },
      ],
    },
    fieldOptions: {
      fields: [
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Text',
          model: 'text',
          validator: 'string',
          required: true,
        },
        {
          type: 'customInput',
          inputType: 'text',
          styleClasses: 'form__field',
          label: 'Position',
          model: 'position',
        },
        {
          type: 'customCheckbox',
          styleClasses: 'form__field',
          label: 'Has Input Box?',
          model: 'has_input_box',
        },
        {
          type: 'customCheckbox',
          styleClasses: 'form__field',
          label: 'Is Email Address?',
          model: 'is_email_address',
          visible: (model) => {
            return model.has_input_box;
          },
        },
        {
          type: 'customCheckbox',
          styleClasses: 'form__field',
          label: 'Required?',
          model: 'required',
        },
        {
          model: '',
          type: 'customSubmit',
          styleClasses: 'form__submit form__submit--first',
          buttonText: 'Save Option',
          onSubmit: (model, schema, context, form) => {
            context.saveObject(model, form);
          },
          validateBeforeSubmit: true,
        },
        {
          model: '',
          type: 'customSubmit',
          styleClasses: 'form__submit',
          buttonText: 'Cancel',
          visible: (model) => {
            return Object.keys(model).length !== 0;
          },
          onSubmit: (model, schema, context) => {
            context.clearForm();
          },
        },
      ],
    },
  },
};

const getters = {
  activeField: state => state.fields.find(field => field.id === state.activeField) || {},
  activeFields: state => state.fields,
  activeFieldId: state => state.fields.findIndex(field => field.id === state.activeField),
  fieldOptionId: (state, getters) => (fieldOption) => {
    const optionId = getters.activeField.options.findIndex(option => option.id === fieldOption);
    return optionId >= 0 ? optionId : 0;
  },
  fieldIndexById: state => (fieldId) => {
    const fieldIndex = state.fields.findIndex(field => field.id === fieldId);
    return fieldIndex >= 0 ? fieldIndex : 0;
  },
};

const mutations = {
  setFields(state, fields) {
    state.fields = fields;
  },

  setFieldOptions(state, { fieldId, options }) {
    if (state.fields[fieldId]) {
      Vue.set(state.fields[fieldId], 'options', options);
    }
  },

  setActiveField(state, field) {
    state.activeField = field;
  },

  setFieldsLoadingState(state, loading) {
    state.fieldsLoading = loading;
  },

  addField(state, field) {
    state.fields.push(field);
  },

  replaceField(state, { fieldIndex, field }) {
    Vue.set(state.fields, fieldIndex, field);
  },

  editField(state, { fieldId, editedField }) {
    Vue.set(state.fields, fieldId, editedField);
  },

  deleteField(state, { fieldId }) {
    Vue.delete(state.fields, fieldId);
  },

  addFieldOption(state, { fieldId, fieldOption }) {
    state.fields[fieldId].options.push(fieldOption);
  },

  editFieldOption(state, { fieldId, fieldOptionId, fieldOption }) {
    Vue.set(state.fields[fieldId].options, fieldOptionId, fieldOption);
  },

  deleteFieldOption(state, { fieldId, fieldOptionId }) {
    Vue.delete(state.fields[fieldId].options, fieldOptionId);
  },
};

const actions = {
  fetchFields({ commit }, pageId) {
    commit('setFieldsLoadingState', true);
    return Vue.axios.get(`admin/pages/${pageId}/fields`)
      .then((response) => {
        commit('setFields', response.data.field);
        commit('setFieldsLoadingState', false);
      })
      .catch();
  },

  fetchFieldOptions({ state, getters, commit }, field) {
    return Vue.axios.get(`admin/fields/${field}/options`)
      .then((response) => {
        commit('setFieldOptions', { fieldId: getters.fieldIndexById(field), options: response.data.option });
      })
      .catch();
  },

  addField({ state, commit, dispatch }, { data, parentId, form }) {
    commit('addField', data);
    const fieldIndex = state.fields.length - 1;
    return Vue.axios.post(`admin/pages/${parentId}/fields`, data)
      .then((response) => {
        commit('replaceField', { fieldIndex, field: response.data.field });
        dispatch('setMessage', { text: 'Successfully created field' }, { root: true });
      })
      .catch(error => Common.handleError(dispatch, error.response, form));
  },

  editField({ getters, commit, dispatch }, { data, form, noCommit }) {
    if (!noCommit) {
      commit('editField', { fieldId: getters.activeFieldId, editedField: data });
    }
    return Vue.axios.put(`admin/fields/${data.id}`, data)
      .then(() => {
        if (!noCommit) {
          dispatch('setMessage', { text: `Successfully edited ${data.title}` }, { root: true });
        }
      })
      .catch(error => Common.handleError(dispatch, error.response, form));
  },

  deleteField({ state, getters, commit, dispatch }, { pageId, fieldId }) {
    commit('deleteField', { fieldId: getters.fieldIndexById(fieldId) });
    return Vue.axios.delete(`admin/pages/${pageId}/fields/${fieldId}`)
      .then(() => dispatch('setMessage', { text: 'Successfully deleted field' }, { root: true }))
      .catch(error => Common.handleError(dispatch, error.response));
  },

  addFieldOption({ state, getters, commit, dispatch }, { data, form }) {
    return Vue.axios.post(`admin/fields/${state.activeField}/options`, data)
      .then((response) => {
        commit('addFieldOption', { fieldId: getters.activeFieldId, fieldOption: response.data.option });
        dispatch('setMessage', { text: 'Successfully created field option' }, { root: true });
      })
      .catch(error => Common.handleError(dispatch, error.response, form));
  },

  editFieldOption({ state, getters, commit, dispatch }, { data, form, noCommit }) {
    return Vue.axios.put(`admin/fields/${state.activeField}/options/${data.id}`, data)
      .then(() => {
        if (!noCommit) {
          commit('editFieldOption', {
            fieldId: getters.activeFieldId,
            fieldOptionId: getters.fieldOptionId(data.id),
            fieldOption: data,
          });
          dispatch('setMessage', { text: `Successfully edited ${data.text}` }, { root: true });
        }
      })
      .catch(error => Common.handleError(dispatch, error.response, form));
  },

  deleteFieldOption({ state, getters, commit, dispatch }, { inlineId }) {
    commit('deleteFieldOption', {
      fieldId: getters.activeFieldId,
      fieldOptionId: getters.fieldOptionId(inlineId),
    });
    return Vue.axios.delete(`admin/fields/${state.activeField}/options/${inlineId}`)
      .then(() => dispatch('setMessage', { text: 'Successfully deleted field option' }, { root: true }))
      .catch(error => Common.handleError(dispatch, error.response));
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
